import {
  FieldHtmlNameUtil, HekateDisplayField,
} from 'www-shared';
import { toRefs, watch } from 'vue';
import UnifiedSignupScreenComponent from '../UnifiedSignupScreenComponent';
import UnifiedSignupScreenName from '../UnifiedSignupScreenName';
import AbstractSubmitSignupScreen from './AbstractSubmitSignupScreen';
import UnifiedSignupPlan from '../session-info/UnifiedSignupPlan';

export default class ProDetails2SignupScreen extends AbstractSubmitSignupScreen {
  public readonly screenName: UnifiedSignupScreenName = 'uss-details-pro-2of2';

  public getThisScreensVisibleComponents(): UnifiedSignupScreenComponent[] {
    const addresses = [
      this.getField(FieldHtmlNameUtil.addEmail1),
      this.getField(FieldHtmlNameUtil.addEmail2),
      this.getField(FieldHtmlNameUtil.addEmail3),
      this.getField(FieldHtmlNameUtil.addEmail4),
      this.getField(FieldHtmlNameUtil.addEmail5),
    ];
    addresses.forEach((field) => { field.autocomplete = 'off'; });

    return [
      new UnifiedSignupScreenComponent({
        displayField: this.getField(FieldHtmlNameUtil.currentCadSystem).shuffleOptionList(),
      }),
      new UnifiedSignupScreenComponent({
        displayField: this.getField(FieldHtmlNameUtil.howManyCadUsersAtCompany),
      }),
      new UnifiedSignupScreenComponent({
        displayField: this.getField(FieldHtmlNameUtil.howManyUserToTrial),
      }),
      new UnifiedSignupScreenComponent({
        displayField: this.getField(FieldHtmlNameUtil.addEmail1),
      }),
      new UnifiedSignupScreenComponent({
        displayField: this.getField(FieldHtmlNameUtil.addEmail2),
      }),
      new UnifiedSignupScreenComponent({
        displayField: this.getField(FieldHtmlNameUtil.addEmail3),
      }),
      new UnifiedSignupScreenComponent({
        displayField: this.getField(FieldHtmlNameUtil.addEmail4),
      }),
      new UnifiedSignupScreenComponent({
        displayField: this.getField(FieldHtmlNameUtil.addEmail5),
      }),
      new UnifiedSignupScreenComponent({
        displayField: this.getField(FieldHtmlNameUtil.itarReg),
      }),
      new UnifiedSignupScreenComponent({
        displayField: this.getField(FieldHtmlNameUtil.howDidYouHearAboutUs),
      }),
      new UnifiedSignupScreenComponent({
        displayField: this.getField(FieldHtmlNameUtil.tosConsentAccepted),
      }),
      new UnifiedSignupScreenComponent({
        displayField: this.getField(FieldHtmlNameUtil.privacyConsentAccepted),
      }),
      new UnifiedSignupScreenComponent({
        displayField: this.getField(FieldHtmlNameUtil.recaptcha),
      }),
    ];
  }

  public predicate(): boolean {
    const { chosenPlan, completedDetailsScreens, formSubmitStatus } = this.sessionInfo.value;
    if (formSubmitStatus !== null && formSubmitStatus !== 'submit-failed') {
      return false;
    }
    if (chosenPlan === null || !chosenPlan.compareTo(UnifiedSignupPlan.pro)) {
      return false;
    }
    if (completedDetailsScreens.length !== 1) {
      return false;
    }

    return true;
  }

  // eslint-disable-next-line class-methods-use-this
  public getProgress(): number {
    return 85;
  }

  // eslint-disable-next-line class-methods-use-this
  public async clearAddedEmailFields(addresses: HekateDisplayField[]) {
    addresses.forEach((field) => {
      field.val = '';
      field.componentName = 'hidden-field';
      field.isRequired = false;
    });
  }

  public async initWatchers() {
    // add ITAR field if the user is in the US
    const geoCountry = this.getField(FieldHtmlNameUtil.geoCountry);
    watch(geoCountry, (newVal) => {
      const itarRegField = this.getField(FieldHtmlNameUtil.itarReg);
      if (`${newVal.val}`.toLocaleLowerCase() === 'United States'.toLocaleLowerCase()) {
        itarRegField.componentName = itarRegField.initialComponentName;
        itarRegField.isRequired = true;
      } else {
        itarRegField.componentName = 'hidden-field';
        itarRegField.isRequired = false;
        itarRegField.val = '';
      }
    }, { immediate: true });

    // map edu position to job title
    const howManyUserToTrialVal = toRefs(this.getField(FieldHtmlNameUtil.howManyUserToTrial)).val;
    const addresses = [
      this.getField(FieldHtmlNameUtil.addEmail1),
      this.getField(FieldHtmlNameUtil.addEmail2),
      this.getField(FieldHtmlNameUtil.addEmail3),
      this.getField(FieldHtmlNameUtil.addEmail4),
      this.getField(FieldHtmlNameUtil.addEmail5),
    ];

    // remove the value of the address if the address is the same as the users address
    const usersEmail = this.getField(FieldHtmlNameUtil.email).val;
    const addressValues = addresses.map((field) => toRefs(field).val);
    watch(addressValues, (newVal) => {
      const findIndex = newVal.findIndex((val) => `${val}`.toLocaleLowerCase() === `${usersEmail}`.toLocaleLowerCase());
      if (findIndex === -1) {
        return;
      }
      addresses[findIndex].val = '';
    });

    this.clearAddedEmailFields(addresses);
    watch(howManyUserToTrialVal, (newVal) => {
      if (!newVal) {
        this.clearAddedEmailFields(addresses);
        return;
      }
      const newValNum = parseInt(`${newVal}`, 10);
      const updatedAddressesValues = [];
      for (let i = 0; i < addresses.length; i += 1) {
        if (updatedAddressesValues.length >= newValNum || !addresses[i].val) {
          // eslint-disable-next-line no-continue
          continue;
        }
        updatedAddressesValues.push(addresses[i].val);
      }
      this.clearAddedEmailFields(addresses);
      for (let i = 0; i < newValNum; i += 1) {
        addresses[i].componentName = addresses[i].initialComponentName;
        addresses[i].isRequired = false;
        if (updatedAddressesValues.length > 0) {
          const fieldValue = updatedAddressesValues.shift();
          if (fieldValue) {
            addresses[i].val = fieldValue;
          }
        }
      }
    });
  }
}
